import React from "react";
import { formatDateForAPI } from "@/lib/date-utils";
import {
  Download,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Camera,
  MapPin,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MetricCard } from "@/components/common/metric-card";
import { GlobalFilterBar } from "@/components/common/global-filter-bar";
import { analyticsAPI } from "@/api";
import { useApi } from "@/hooks";
import { useDateRange } from "@/contexts/DateRangeContext";
import { formatNumber } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Drill-down view state type
type DrillDownView =
  | { level: "overview" }
  | { level: "zone"; zoneId: string }
  | { level: "camera"; zoneId: string; cameraId: string };

export function Misreads() {
  const { dateRange, selectedLocations } = useDateRange();
  const [drillDown, setDrillDown] = React.useState<DrillDownView>({
    level: "overview",
  });

  const locationParam = React.useMemo(
    () =>
      selectedLocations.length > 0 ? selectedLocations.join(",") : undefined,
    [selectedLocations],
  );

  const startTimestamp = dateRange.from.getTime();
  const endTimestamp = dateRange.to.getTime();

  const {
    data: misreadData,
    isLoading,
    execute: fetchMisreads,
  } = useApi(analyticsAPI.getMisreadMetrics);

  const lastFetchKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const key = `${startTimestamp}:${endTimestamp}:${locationParam ?? "all"}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;

    fetchMisreads({
      start_date: new Date(startTimestamp).toISOString(),
      end_date: new Date(endTimestamp).toISOString(),
      ...(locationParam ? { location: locationParam } : {}),
    }).catch((error) => {
      console.error("Failed to fetch misread metrics:", error);
      lastFetchKeyRef.current = null;
    });
  }, [fetchMisreads, startTimestamp, endTimestamp, locationParam]);

  // Reset drill-down when filters change
  React.useEffect(() => {
    setDrillDown({ level: "overview" });
  }, [startTimestamp, endTimestamp, locationParam]);

  const summary = misreadData?.summary || {
    total_reads: 0,
    total_misreads: 0,
    overall_rate: 0,
  };
  const dailyData = misreadData?.daily || [];
  const byLocation = misreadData?.by_location || [];

  // Calculate trend from daily data
  const calculateTrend = (data: typeof dailyData) => {
    if (data.length < 14) return { direction: "stable" as const, change: 0 };
    const recent = data.slice(-7);
    const previous = data.slice(-14, -7);
    const recentAvg =
      recent.reduce((sum, d) => sum + d.misread_rate, 0) / recent.length;
    const previousAvg =
      previous.reduce((sum, d) => sum + d.misread_rate, 0) / previous.length;
    const change = recentAvg - previousAvg;
    return {
      direction:
        change < -0.1
          ? ("improving" as const)
          : change > 0.1
            ? ("worsening" as const)
            : ("stable" as const),
      change: Math.abs(change),
    };
  };
  const overallTrend = calculateTrend(dailyData);

  // Get current view data
  const selectedZone =
    drillDown.level !== "overview"
      ? byLocation.find((loc: any) => loc.location_id === drillDown.zoneId)
      : null;
  const selectedCamera =
    drillDown.level === "camera" && selectedZone
      ? selectedZone.cameras?.find(
          (cam: any) => cam.camera_id === drillDown.cameraId,
        )
      : null;

  // Get current chart data based on drill-down level
  const getCurrentChartData = () => {
    if (drillDown.level === "camera" && selectedCamera?.daily) {
      return selectedCamera.daily;
    }
    if (drillDown.level === "zone" && selectedZone?.daily) {
      return selectedZone.daily;
    }
    return dailyData;
  };
  const currentChartData = getCurrentChartData();
  const currentTrend = calculateTrend(currentChartData);

  // Get current title/description
  const getChartTitle = () => {
    if (drillDown.level === "camera") {
      return `Camera ${drillDown.cameraId} Trend`;
    }
    if (drillDown.level === "zone") {
      return `Zone ${drillDown.zoneId} Trend`;
    }
    return "Overall Misread Rate Trend";
  };

  const handleExport = () => {
    if (!currentChartData.length) return;

    const prefix =
      drillDown.level === "camera"
        ? `camera_${drillDown.cameraId}`
        : drillDown.level === "zone"
          ? `zone_${drillDown.zoneId}`
          : "overall";

    const headers = ["Date", "Total Reads", "Misreads", "Misread Rate (%)"];
    const rows = currentChartData.map((d: any) => [
      d.date,
      d.total_reads,
      d.misread_count,
      d.misread_rate,
    ]);
    const csv = [headers.join(","), ...rows.map((r: any) => r.join(","))].join(
      "\n",
    );

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `misreads_${prefix}_${formatDateForAPI(dateRange.from).split("T")[0]}_${formatDateForAPI(dateRange.to).split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Color scale for misread rate (green = low/good, red = high/bad)
  const getRateColor = (rate: number) => {
    if (rate < 1) return "#10b981"; // green
    if (rate < 3) return "#f59e0b"; // amber
    return "#ef4444"; // red
  };

  // Navigation handlers
  const handleZoneClick = (zoneId: string) => {
    setDrillDown({ level: "zone", zoneId });
  };

  const handleCameraClick = (zoneId: string, cameraId: string) => {
    setDrillDown({ level: "camera", zoneId, cameraId });
  };

  const handleBack = () => {
    if (drillDown.level === "camera") {
      setDrillDown({ level: "zone", zoneId: drillDown.zoneId });
    } else {
      setDrillDown({ level: "overview" });
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Misreads Analytics
          </h1>
          <p className="text-muted-foreground">
            Track OCR misread patterns by zone and camera over time (Nebraska O
            vs 0)
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!currentChartData.length}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilterBar />

      {/* Breadcrumb Navigation */}
      {drillDown.level !== "overview" && (
        <div className="flex items-center gap-2 text-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <span className="text-muted-foreground">/</span>
          <button
            onClick={() => setDrillDown({ level: "overview" })}
            className="text-muted-foreground hover:text-foreground"
          >
            All Zones
          </button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <button
            onClick={() =>
              setDrillDown({ level: "zone", zoneId: drillDown.zoneId })
            }
            className={
              drillDown.level === "zone"
                ? "font-medium"
                : "text-muted-foreground hover:text-foreground"
            }
          >
            Zone {drillDown.zoneId}
          </button>
          {drillDown.level === "camera" && (
            <>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Camera {drillDown.cameraId}</span>
            </>
          )}
        </div>
      )}

      {/* Summary KPIs */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total NE Reads"
          value={formatNumber(summary.total_reads)}
          description="Nebraska plate reads analyzed"
          icon={Camera}
          loading={isLoading}
        />
        <MetricCard
          title="Misreads Detected"
          value={formatNumber(summary.total_misreads)}
          description="Plates with letter O (should be 0)"
          icon={AlertTriangle}
          loading={isLoading}
          badge={
            summary.total_misreads > 0
              ? { text: "Review", variant: "warning" }
              : undefined
          }
        />
        <MetricCard
          title="Misread Rate"
          value={`${summary.overall_rate}%`}
          description="Percentage of NE reads with O"
          icon={
            overallTrend.direction === "improving" ? TrendingDown : TrendingUp
          }
          loading={isLoading}
          badge={
            overallTrend.direction === "improving"
              ? { text: "Improving", variant: "success" }
              : overallTrend.direction === "worsening"
                ? { text: "Worsening", variant: "destructive" }
                : undefined
          }
        />
        <MetricCard
          title="Zones Analyzed"
          value={byLocation.length}
          description="Zones with NE plate data"
          icon={MapPin}
          loading={isLoading}
        />
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{getChartTitle()}</CardTitle>
              <CardDescription>
                {drillDown.level === "camera"
                  ? `Daily misread rate for camera ${drillDown.cameraId} in Zone ${drillDown.zoneId}`
                  : drillDown.level === "zone"
                    ? `Daily misread rate for Zone ${drillDown.zoneId} (click a camera below to drill down)`
                    : "Daily percentage of Nebraska plates containing letter O (click a zone below to drill down)"}
              </CardDescription>
            </div>
            {currentTrend.direction !== "stable" && (
              <Badge
                variant={
                  currentTrend.direction === "improving"
                    ? "default"
                    : "destructive"
                }
              >
                {currentTrend.direction === "improving"
                  ? "Improving"
                  : "Worsening"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-80">
              <div className="text-muted-foreground">Loading trend data...</div>
            </div>
          ) : currentChartData.length === 0 ? (
            <div className="flex items-center justify-center h-80">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <div className="text-lg font-semibold">No Data Available</div>
                <div className="text-sm text-muted-foreground">
                  No Nebraska plate reads found for the selected period.
                </div>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={currentChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) =>
                    new Date(value).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  }
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-background border rounded-lg shadow-lg p-3">
                        <p className="font-semibold">
                          {new Date(label).toLocaleDateString()}
                        </p>
                        <p className="text-sm">
                          Total Reads: {formatNumber(data.total_reads)}
                        </p>
                        <p className="text-sm">
                          Misreads: {formatNumber(data.misread_count)}
                        </p>
                        <p
                          className="text-sm font-medium"
                          style={{ color: getRateColor(data.misread_rate) }}
                        >
                          Rate: {data.misread_rate}%
                        </p>
                      </div>
                    );
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="misread_rate"
                  name="Misread Rate (%)"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: "#ef4444", strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Drill-down List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {drillDown.level === "zone" ? (
              <>
                <Camera className="h-5 w-5" />
                Cameras in Zone {drillDown.zoneId}
              </>
            ) : (
              <>
                <MapPin className="h-5 w-5" />
                Zones
              </>
            )}
          </CardTitle>
          <CardDescription>
            {drillDown.level === "zone"
              ? "Click a camera to see its trend over time"
              : "Click a zone to see its cameras and drill down further"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : drillDown.level === "zone" && selectedZone ? (
            // Show cameras for selected zone
            <div className="space-y-2">
              {selectedZone.cameras?.length === 0 ? (
                <div className="text-muted-foreground text-center py-8">
                  No cameras found in this zone
                </div>
              ) : (
                selectedZone.cameras?.map((cam: any) => (
                  <button
                    key={cam.camera_id}
                    onClick={() =>
                      handleCameraClick(drillDown.zoneId, cam.camera_id)
                    }
                    className="w-full flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Camera className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{cam.camera_id}</span>
                        <p className="text-xs text-muted-foreground">
                          {formatNumber(cam.total_reads)} reads,{" "}
                          {formatNumber(cam.misread_count)} misreads
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        style={{
                          borderColor: getRateColor(cam.misread_rate),
                          color: getRateColor(cam.misread_rate),
                        }}
                      >
                        {cam.misread_rate}%
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : drillDown.level === "camera" ? (
            // At camera level, show camera details
            <div className="text-center py-8 text-muted-foreground">
              <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Viewing camera {drillDown.cameraId} trend above</p>
              <p className="text-sm">
                Use the Back button to return to zone view
              </p>
            </div>
          ) : byLocation.length === 0 ? (
            <div className="text-muted-foreground text-center py-8">
              No zone data available
            </div>
          ) : (
            // Show all zones
            <div className="space-y-2">
              {byLocation.map((loc: any) => (
                <button
                  key={loc.location_id}
                  onClick={() => handleZoneClick(loc.location_id)}
                  className="w-full flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <span className="font-medium">
                        Zone {loc.location_id}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(loc.total_reads)} reads,{" "}
                        {formatNumber(loc.misread_count)} misreads
                        {loc.cameras?.length > 0 &&
                          ` · ${loc.cameras.length} cameras`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      style={{
                        borderColor: getRateColor(loc.misread_rate),
                        color: getRateColor(loc.misread_rate),
                      }}
                    >
                      {loc.misread_rate}%
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insights Section */}
      <Card>
        <CardHeader>
          <CardTitle>Insights</CardTitle>
          <CardDescription>
            {drillDown.level === "camera"
              ? `Monthly story for Camera ${drillDown.cameraId}`
              : drillDown.level === "zone"
                ? `Monthly story for Zone ${drillDown.zoneId}`
                : "Monthly story across all zones"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground">Loading insights...</div>
          ) : currentChartData.length === 0 ? (
            <div className="text-muted-foreground">
              No data available for insights.
            </div>
          ) : (
            <InsightsNarrative
              dailyData={currentChartData}
              level={drillDown.level}
              zoneId={
                drillDown.level !== "overview" ? drillDown.zoneId : undefined
              }
              cameraId={
                drillDown.level === "camera" ? drillDown.cameraId : undefined
              }
              byLocation={byLocation}
            />
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>About Misread Detection</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>Nebraska Rule:</strong> Nebraska license plates never
            contain the letter "O" - only the number "0" (zero). When the LPR
            system reads a Nebraska plate with the letter O, it indicates an OCR
            misread.
          </p>
          <p>
            <strong>Note:</strong> Vanity/personalized plates may legitimately
            contain the letter O. This metric captures all plates with O, which
            mostly indicates OCR confusion between O and 0.
          </p>
          <p>
            <strong>Color coding:</strong> Green (&lt;1%) = Good, Amber (1-3%) =
            Moderate, Red (&gt;3%) = Needs attention.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper component to generate monthly narrative insights
function InsightsNarrative({
  dailyData,
  level,
  zoneId,
  cameraId,
  byLocation,
}: {
  dailyData: any[];
  level: string;
  zoneId?: string;
  cameraId?: string;
  byLocation: any[];
}) {
  // Group data by month
  const monthlyData = React.useMemo(() => {
    const grouped: Record<
      string,
      { total_reads: number; misread_count: number; days: number }
    > = {};

    dailyData.forEach((day: any) => {
      const month = day.date.substring(0, 7); // YYYY-MM
      if (!grouped[month]) {
        grouped[month] = { total_reads: 0, misread_count: 0, days: 0 };
      }
      grouped[month].total_reads += day.total_reads;
      grouped[month].misread_count += day.misread_count;
      grouped[month].days += 1;
    });

    return Object.entries(grouped)
      .map(([month, data]) => ({
        month,
        monthLabel: new Date(month + "-01").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
        ...data,
        rate:
          data.total_reads > 0
            ? (data.misread_count / data.total_reads) * 100
            : 0,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [dailyData]);

  // Generate narrative for each month
  const generateMonthNarrative = (
    current: (typeof monthlyData)[0],
    prev: (typeof monthlyData)[0] | null,
    index: number,
  ) => {
    const parts: string[] = [];

    // Volume context
    parts.push(`${formatNumber(current.total_reads)} NE plate reads`);

    // Misread info
    if (current.misread_count === 0) {
      parts.push("with zero misreads detected");
    } else {
      parts.push(
        `with ${formatNumber(current.misread_count)} misreads (${current.rate.toFixed(2)}%)`,
      );
    }

    // Comparison to previous month
    if (prev && prev.total_reads > 0 && current.total_reads > 0) {
      const rateChange = current.rate - prev.rate;
      const volumeChange =
        ((current.total_reads - prev.total_reads) / prev.total_reads) * 100;

      if (Math.abs(rateChange) > 0.05) {
        if (rateChange < 0) {
          parts.push(
            `— improved by ${Math.abs(rateChange).toFixed(2)}pp from ${prev.monthLabel}`,
          );
        } else {
          parts.push(
            `— worsened by ${rateChange.toFixed(2)}pp from ${prev.monthLabel}`,
          );
        }
      }

      if (Math.abs(volumeChange) > 10) {
        if (volumeChange > 0) {
          parts.push(`(volume up ${volumeChange.toFixed(0)}%)`);
        } else {
          parts.push(`(volume down ${Math.abs(volumeChange).toFixed(0)}%)`);
        }
      }
    }

    return parts.join(" ");
  };

  // Get zone/camera comparison insights for overview level
  const getComparisonInsights = () => {
    if (level !== "overview" || byLocation.length === 0) return null;

    const sorted = [...byLocation].sort(
      (a: any, b: any) => b.misread_rate - a.misread_rate,
    );
    const worst = sorted[0];
    const best = sorted[sorted.length - 1];

    if (!worst || !best || worst.location_id === best.location_id) return null;

    return (
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm font-medium mb-2">Zone Comparison:</p>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>
            <span className="text-red-600 font-medium">Highest rate:</span> Zone{" "}
            {worst.location_id} at {worst.misread_rate}% (
            {formatNumber(worst.misread_count)} of{" "}
            {formatNumber(worst.total_reads)} reads)
          </li>
          <li>
            <span className="text-green-600 font-medium">Lowest rate:</span>{" "}
            Zone {best.location_id} at {best.misread_rate}% (
            {formatNumber(best.misread_count)} of{" "}
            {formatNumber(best.total_reads)} reads)
          </li>
        </ul>
      </div>
    );
  };

  // Get camera insights when viewing a zone
  const getCameraInsights = () => {
    if (level !== "zone" || !zoneId) return null;

    const zone = byLocation.find((loc: any) => loc.location_id === zoneId);
    if (!zone?.cameras || zone.cameras.length === 0) return null;

    const sorted = [...zone.cameras].sort(
      (a: any, b: any) => b.misread_rate - a.misread_rate,
    );
    const worst = sorted[0];
    const best = sorted[sorted.length - 1];

    if (!worst || !best || worst.camera_id === best.camera_id) return null;

    return (
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm font-medium mb-2">
          Camera Comparison in Zone {zoneId}:
        </p>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>
            <span className="text-red-600 font-medium">Highest rate:</span>{" "}
            {worst.camera_id} at {worst.misread_rate}%
          </li>
          <li>
            <span className="text-green-600 font-medium">Lowest rate:</span>{" "}
            {best.camera_id} at {best.misread_rate}%
          </li>
        </ul>
      </div>
    );
  };

  // Overall trend assessment
  const getOverallAssessment = () => {
    if (monthlyData.length < 2) return null;

    const first = monthlyData[0];
    const last = monthlyData[monthlyData.length - 1];
    const rateChange = last.rate - first.rate;

    let assessment = "";
    if (Math.abs(rateChange) < 0.02) {
      assessment =
        "Misread rate has remained relatively stable over this period.";
    } else if (rateChange < 0) {
      assessment = `Misread rate has improved by ${Math.abs(rateChange).toFixed(2)} percentage points from ${first.monthLabel} to ${last.monthLabel}.`;
    } else {
      assessment = `Misread rate has increased by ${rateChange.toFixed(2)} percentage points from ${first.monthLabel} to ${last.monthLabel}.`;
    }

    return (
      <div className="mb-4 p-3 border-l-4 border-primary bg-muted/30 rounded-r-lg">
        <p className="text-sm font-medium">{assessment}</p>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {getOverallAssessment()}

      <div className="space-y-2">
        {monthlyData.map((month, index) => (
          <div key={month.month} className="flex gap-3 text-sm">
            <span className="font-medium text-muted-foreground w-32 shrink-0">
              {month.monthLabel}:
            </span>
            <span
              className={
                month.rate > 1
                  ? "text-amber-600"
                  : month.rate > 0
                    ? "text-muted-foreground"
                    : "text-green-600"
              }
            >
              {generateMonthNarrative(
                month,
                index > 0 ? monthlyData[index - 1] : null,
                index,
              )}
            </span>
          </div>
        ))}
      </div>

      {getComparisonInsights()}
      {getCameraInsights()}
    </div>
  );
}
